import React from 'react';
import { ARCH_ALL, GalleryFilterCtl } from './useGalleryFilter';
import { CategoryCascade, useCategoryTree } from '../../components/MaterialFieldInputs';
import { deviceLabel } from '../../api/device';
import { useDictText } from '../../components/DictSelect';
import { useScopedDictText } from '../../utils/dictOptions';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';

// 效果图 / 矩阵图 tab 共用的过滤卡 UI, 状态由 useGalleryFilter 提供.
// 材质筛选没有输入框 (由材料详情 ?xcode= 链接预填), 生效时显示可清除的 chip.

export default function GalleryFilterBar({ f, total }: { f: GalleryFilterCtl; total: number }) {
    const categoryTree = useCategoryTree();
    // 光源下拉选项来自 facets 接口 (是 code), 展示走 light_source 字典中文名
    const lightText = useDictText('light_source');
    // 加工类型选项同为 facets code, 展示走 process_type 字典 label;
    // 选了具体架构只拉该 scope, "全部架构"时逐框架加载后合并查 (同 code 跨框架同义)
    const ptScopes = f.archCode !== ARCH_ALL ? [f.archCode] : f.archs.map((a) => a.code);
    const processTypeText = useScopedDictText('process_type', ptScopes);
    return (
        <div className="card p-3">
            <div className="flex flex-wrap gap-3 items-end">
                <div>
                    <label className="field-label">关键词</label>
                    <input
                        type="search"
                        className="inp w-48"
                        value={f.keyword}
                        onChange={(e) => {
                            f.setKeyword(e.target.value);
                            f.setPage(1);
                        }}
                        placeholder="搜 材料 xcode / 设备"
                    />
                </div>
                <div>
                    <label className="field-label">工艺架构</label>
                    <SearchSelect
                        className="w-40"
                        value={f.archCode === ARCH_ALL ? '' : f.archCode}
                        onChange={(v) => f.onSetArch(v || ARCH_ALL)}
                        placeholder="全部架构"
                        options={f.archs.map((a) => ({ value: a.code, label: `${a.icon} ${a.label}` }))}
                        clearable
                    />
                </div>
                <div>
                    <label className="field-label">机型</label>
                    <SearchSelect
                        className="w-32"
                        value={f.family}
                        onChange={(newFamily) => {
                            f.setFamily(newFamily);
                            if (newFamily && f.deviceKey) {
                                const stillValid = f.devices.some(
                                    (d) => d.family === newFamily && d.deviceKey === f.deviceKey,
                                );
                                if (!stillValid) f.setDeviceKey('');
                            }
                            f.setPage(1);
                        }}
                        placeholder="全部"
                        options={Array.from(new Set(f.devices.map((d) => d.family)))
                            .filter(Boolean)
                            .sort()
                            .map((fam) => ({ value: fam, label: fam }))}
                        clearable
                    />
                </div>
                <div>
                    <label className="field-label">型号</label>
                    <SearchSelect
                        className="w-52"
                        value={f.deviceKey}
                        onChange={(v) => {
                            f.setDeviceKey(v);
                            const dev = f.devices.find((d) => d.deviceKey === v);
                            if (dev) f.setFamily(dev.family);
                            f.setPage(1);
                        }}
                        placeholder="全部"
                        options={f.devices
                            .filter((d) => !f.family || d.family === f.family)
                            .map((d) => ({ value: d.deviceKey, label: deviceLabel(d, lightText) }))}
                        clearable
                    />
                </div>
                <div>
                    <label className="field-label">加工类型</label>
                    <SearchSelect
                        className="w-40"
                        value={f.processType}
                        onChange={(v) => {
                            f.setProcessType(v);
                            f.setPage(1);
                        }}
                        placeholder="全部类型"
                        options={zhOptions(
                            f.modeOpts,
                            (m) => m,
                            (m) => processTypeText(f.archCode !== ARCH_ALL ? f.archCode : undefined, m),
                        )}
                        clearable
                    />
                </div>
                <div>
                    <label className="field-label">材料分类</label>
                    <span className="flex gap-1">
                        <CategoryCascade
                            tree={categoryTree}
                            type1={f.type1}
                            type2={f.type2}
                            onChange={(t1, t2) => f.setTypes(t1, t2)}
                            searchable
                            selectCls="w-36"
                            placeholders={['全部一级', '全部二级']}
                        />
                    </span>
                </div>
                <div>
                    <label className="field-label">光源</label>
                    <SearchSelect
                        className="w-32"
                        value={f.lightSource}
                        onChange={(v) => {
                            f.setLightSource(v);
                            f.setPage(1);
                        }}
                        placeholder="全部光源"
                        options={zhOptions(f.lightOpts, (l) => l, (l) => lightText(l))}
                        clearable
                    />
                </div>
                {f.xcode && (
                    <span className="tag bg-brand-50 text-brand-700 whitespace-nowrap self-center">
                        材质 {f.xcode}
                        <button
                            type="button"
                            className="ml-1 hover:text-brand-900"
                            title="清除材质筛选"
                            onClick={() => {
                                f.setXcode('');
                                f.setPage(1);
                            }}
                        >
                            ✕
                        </button>
                    </span>
                )}
                <button
                    type="button"
                    className="text-xs px-3 py-2 rounded border border-slate-200 hover:bg-slate-50"
                    onClick={f.onReset}
                >
                    重置
                </button>
                <span className="ml-auto text-xs text-slate-400">共 {total} 张</span>
            </div>
        </div>
    );
}
